import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isEmployee, isPlatformAdmin } from '../lib/roles';
import { toast } from 'sonner';
import { Phone, Mail, RefreshCw, Search } from 'lucide-react';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'paid_pending_lender_approval', label: 'Awaiting lender' },
  { value: 'paid', label: 'Approved / Paid' },
  { value: 'ready_for_pickup', label: 'Ready for pickup' },
  { value: 'rejected_by_lender', label: 'Declined' },
];

const OperationsOrdersPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [declineOrderId, setDeclineOrderId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [actingId, setActingId] = useState(null);

  const allowed = isEmployee(user?.role) || isPlatformAdmin(user?.role);

  const fetchOrders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const submitDecline = async (orderId, alsoRefund) => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please type a reason for declining');
      return;
    }
    setActingId(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/lender/reject`,
        { reason, refund: alsoRefund },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Order declined');
      setDeclineOrderId(null);
      setDeclineReason('');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline');
    } finally {
      setActingId(null);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/operations');
      return;
    }
    if (!allowed) {
      navigate('/');
      return;
    }
    fetchOrders();
  }, [user, token, allowed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== 'all') {
        const st = (o.status || '').toLowerCase();
        const las = (o.lender_approval_status || '').toLowerCase();
        if (statusFilter === 'paid_pending_lender_approval') {
          if (!(st === 'paid_pending_lender_approval' || las === 'pending')) return false;
        } else if (statusFilter === 'paid') {
          if (!(st === 'paid' || (o.payment_status === 'completed' && las === 'approved'))) return false;
        } else if (st !== statusFilter) {
          return false;
        }
      }
      if (!q) return true;
      const hay = [
        o.id,
        o.customer_name,
        o.customer_email,
        o.customer_phone,
        o.lender_name,
        o.lender_phone,
        o.pickup_code,
        ...(o.items || []).map((i) => i.book_title || i.title),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orders, statusFilter, query]);

  const pendingCount = orders.filter(
    (o) => o.status === 'paid_pending_lender_approval' || o.lender_approval_status === 'pending'
  ).length;

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Operations — All Orders</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Follow up with borrowers and lenders. Filter by awaiting approval, then call or WhatsApp using the contact details below.
            </p>
          </div>
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Total orders</p>
            <p className="text-2xl font-bold">{orders.length}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Awaiting lender approval</p>
            <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Showing</p>
            <p className="text-2xl font-bold">{filtered.length}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search name, phone, order id, book, pickup code…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={statusFilter === f.value ? 'default' : 'outline'}
                onClick={() => setStatusFilter(f.value)}
                style={statusFilter === f.value ? { backgroundColor: 'var(--primary)' } : undefined}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Order</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Borrower</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Lender</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Book</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Pickup</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Loading orders…
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No matching orders.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((order) => {
                    const bookTitle =
                      (order.items || []).map((i) => i.book_title || i.title).filter(Boolean).join(', ') ||
                      '—';
                    const awaiting =
                      order.status === 'paid_pending_lender_approval' ||
                      order.lender_approval_status === 'pending';
                    return (
                      <tr key={order.id} className={awaiting ? 'bg-amber-50/60' : undefined}>
                        <td className="px-4 py-3 align-top">
                          <p className="font-mono text-xs">{order.id?.slice(0, 8)}…</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {order.created_at
                              ? String(order.created_at).replace('T', ' ').slice(0, 16)
                              : ''}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 capitalize">
                            {order.delivery_method || 'delivery'}
                          </p>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <p className="font-medium">{order.customer_name || '—'}</p>
                          {order.customer_phone && (
                            <a
                              href={`tel:${order.customer_phone}`}
                              className="flex items-center gap-1 text-xs text-blue-700 mt-1"
                            >
                              <Phone className="h-3 w-3" />
                              {order.customer_phone}
                            </a>
                          )}
                          {order.customer_email && (
                            <a
                              href={`mailto:${order.customer_email}`}
                              className="flex items-center gap-1 text-xs text-gray-600 mt-0.5"
                            >
                              <Mail className="h-3 w-3" />
                              {order.customer_email}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <p className="font-medium">{order.lender_name || '—'}</p>
                          {order.lender_phone && (
                            <a
                              href={`tel:${order.lender_phone}`}
                              className="flex items-center gap-1 text-xs text-blue-700 mt-1"
                            >
                              <Phone className="h-3 w-3" />
                              {order.lender_phone}
                            </a>
                          )}
                          {order.lender_email && (
                            <a
                              href={`mailto:${order.lender_email}`}
                              className="flex items-center gap-1 text-xs text-gray-600 mt-0.5"
                            >
                              <Mail className="h-3 w-3" />
                              {order.lender_email}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top max-w-[200px]">
                          <p className="line-clamp-2">{bookTitle}</p>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-block text-xs font-semibold px-2 py-1 rounded-full ${
                              awaiting
                                ? 'bg-amber-100 text-amber-800'
                                : order.status === 'rejected_by_lender'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {awaiting
                              ? 'Awaiting lender'
                              : order.status === 'rejected_by_lender'
                                ? 'Declined'
                                : order.status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          {order.pickup_code && !awaiting ? (
                            <span className="font-mono font-bold text-amber-800">{order.pickup_code}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top font-semibold">
                          ₹{Math.round(order.final_amount || 0)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {isPlatformAdmin(user?.role) && order.status === 'paid_pending_lender_approval' && (
                            declineOrderId === order.id ? (
                              <div className="space-y-2 min-w-[160px]">
                                <textarea
                                  value={declineReason}
                                  onChange={(e) => setDeclineReason(e.target.value)}
                                  placeholder="Reason for declining"
                                  className="w-full min-h-[70px] rounded border p-2 text-sm"
                                />
                                <Button size="sm" className="w-full bg-red-600 text-white" disabled={actingId === order.id} onClick={() => submitDecline(order.id, true)}>
                                  Decline and refund
                                </Button>
                                <Button size="sm" variant="outline" className="w-full" onClick={() => setDeclineOrderId(null)}>Cancel</Button>
                              </div>
                            ) : (
                              <Button size="sm" className="bg-red-600 text-white" onClick={() => { setDeclineOrderId(order.id); setDeclineReason(''); }}>
                                Decline
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OperationsOrdersPage;
